/* eslint-disable no-magic-numbers */
import { useCallback } from 'react';
import styled from 'styled-components';
import { Icon, IconName } from '../../icon';
import { DropdownSize } from '../../dropdown/DropdownSize';

// A chip rides inside the field, so it takes its scale from the field's size —
// one table instead of a ternary per property.
type ChipSize = {
	height: number;
	fontSize: number;
	/** The remove cross, which reads small at the label's size. */
	removeSize: number;
	gap: number;
	padding: number;
	/** Tighter on the side the cross sits on. */
	paddingRemove: number;
};

const chipSizes: { [key in DropdownSize]: ChipSize } = {
	compact: { height: 22, fontSize: 13, removeSize: 15, gap: 6, padding: 10, paddingRemove: 6 },
	large: { height: 30, fontSize: 16, removeSize: 18, gap: 8, padding: 12, paddingRemove: 8 },
	default: { height: 30, fontSize: 16, removeSize: 18, gap: 8, padding: 12, paddingRemove: 8 },
	small: { height: 22, fontSize: 13, removeSize: 15, gap: 6, padding: 10, paddingRemove: 6 },
};

const StyledItemLabel = styled.span<{ $canRemove?: boolean, $size: ChipSize }>`
	border-radius: ${props => props.$size.height / 2}px;
	display: flex;
	align-items: center;
	background-color: ${props => props.theme.form.multiSelectInput.itemBackground};
	padding: 0 ${props => props.$canRemove ? props.$size.paddingRemove : props.$size.padding}px
		0 ${props => props.$size.padding}px;
	height: ${props => props.$size.height}px;
	font-size: ${props => props.$size.fontSize}px;
	line-height: 1;
	text-overflow: ellipsis;
	overflow: hidden;
	column-gap: ${props => props.$size.gap}px;
	box-sizing: border-box;
`;

const StyledIcon = styled(Icon)`
	display: flex;
	align-items: center;
	font-size: inherit;
	color: ${props => props.theme.form.multiSelectInput.itemColor};
`;

const StyledIconButton = styled(Icon)<{ $size: ChipSize }>`
	padding: 0;
	border: 0;
	background-color: transparent;
	color: ${props => props.theme.form.multiSelectInput.removeIcon};
	cursor: pointer;
	font-size: ${props => props.$size.removeSize}px;
	display: flex;
	align-items: center;

	&:hover {
		color: ${props => props.theme.form.multiSelectInput.removeIconHover};
	}
`;

const StyledName = styled.span`
	text-overflow: ellipsis;
	overflow: hidden;
	color: ${props => props.theme.form.multiSelectInput.itemColor};
	line-height: 1;
	display: block;
`;

type SimpleInputChipProps = {
	value: string;
	label?: string;
	icon?: IconName;
	onRemove: (value: string) => void;
	canRemove?: boolean;
	size?: DropdownSize;
};

export function SimpleInputChip({ value, label, icon, onRemove, canRemove, size = 'compact' }: SimpleInputChipProps) {
	const onCrossClick = useCallback((e: React.MouseEvent) => {
		e.stopPropagation();
		onRemove(value);
	}, [value, onRemove]);

	const chipSize = chipSizes[size];

	return (
		<StyledItemLabel $canRemove={canRemove} $size={chipSize}>
			{icon && <StyledIcon icon={icon as IconName} />}
			<StyledName>{label || value}</StyledName>
			{canRemove && <StyledIconButton icon={'x-circle-solid'} onClick={onCrossClick} $size={chipSize} />}
		</StyledItemLabel>
	);
}
